
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSettings, FiCheck, FiX, FiShield, FiEye, FiBarChart3, FiUsers } = FiIcons;

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false
  });

  // Check if consent has been given
  useEffect(() => {
    const consent = localStorage.getItem('trbe-cookie-consent');
    const consentTimestamp = localStorage.getItem('trbe-cookie-consent-timestamp');

    if (!consent || !consentTimestamp) {
      // Show banner if no consent given
      setShowBanner(true);
    } else {
      // Check if consent is older than 12 months (GDPR requirement)
      const consentDate = new Date(parseInt(consentTimestamp));
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

      if (consentDate < twelveMonthsAgo) {
        setShowBanner(true);
      } else {
        // Load existing preferences
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
        initializeServices(savedPrefs);
      }
    }

    // Listen for cookie settings open event
    const handleOpenCookieSettings = () => {
      setShowSettings(true);
    };

    window.addEventListener('openCookieSettings', handleOpenCookieSettings);
    
    return () => {
      window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
    };
  }, []);

  const blockNonEssentialScripts = () => {
    // Block Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }

    // Block other tracking scripts
    const scripts = document.querySelectorAll('script[src*="facebook"], script[src*="linkedin"], script[src*="twitter"]');
    scripts.forEach(script => {
      script.remove();
    });
  };

  const initializeServices = (prefs) => {
    // Only initialize services if user has consented
    if (prefs.analytics) {
      initializeAnalytics();
    } else {
      blockNonEssentialScripts();
    }

    if (prefs.marketing) {
      initializeMarketing();
    }

    if (prefs.functional) {
      initializeFunctional();
    }
  };

  const initializeAnalytics = () => {
    // Example: Initialize Google Analytics or other analytics
    console.log('🔍 Analytics initialized');
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const initializeMarketing = () => {
    // Example: Initialize marketing pixels, remarketing
    console.log('📢 Marketing tools initialized');
  };

  const initializeFunctional = () => {
    // Example: Initialize chat widgets, preference cookies
    console.log('⚙️ Functional cookies initialized');
  };

  const saveConsent = (prefs) => {
    const consentData = {
      ...prefs,
      timestamp: Date.now(),
      version: '1.0'
    };

    localStorage.setItem('trbe-cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('trbe-cookie-consent-timestamp', Date.now().toString());
    localStorage.setItem('trbe-cookie-consent-log', JSON.stringify(consentData));

    setPreferences(prefs);
    initializeServices(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    saveConsent(necessaryOnly);
  };

  const handleCustomSave = () => {
    saveConsent(preferences);
  };

  const cookieCategories = [
    {
      id: 'necessary',
      title: 'Necessary Cookies',
      description: 'Essential for website functionality and security. These cannot be disabled.',
      icon: FiShield,
      required: true,
      examples: 'Session management, security tokens, basic functionality'
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      description: 'Enable enhanced features and personalization.',
      icon: FiSettings,
      required: false,
      examples: 'Language preferences, region settings, accessibility options'
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website.',
      icon: FiBarChart3,
      required: false,
      examples: 'Google Analytics, page views, user behavior analysis'
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements and track campaign effectiveness.',
      icon: FiUsers,
      required: false,
      examples: 'Remarketing pixels, social media tracking, advertising platforms'
    }
  ];

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && !showSettings && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiShield} className="h-5 w-5 text-brand-orange" />
                    <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or learn more in our{' '}
                    <a href="/privacy" className="text-brand-orange hover:underline">Privacy Policy</a> and{' '}
                    <a href="/cookies" className="text-brand-orange hover:underline">Cookie Policy</a>.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 min-w-fit">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Customize
                  </button>
                  <button
                    onClick={acceptNecessary}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Necessary Only
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-6 py-2 bg-brand-orange text-white rounded-lg hover:bg-secondary-500 transition-colors text-sm font-medium flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} className="h-4 w-4" />
                    <span>Accept All</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">
                  Manage your cookie preferences. You can enable or disable different types of cookies below.
                </p>
              </div>

              <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
                {cookieCategories.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <SafeIcon icon={category.icon} className="h-5 w-5 text-brand-purple" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {category.title}
                          </h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                          {category.description}
                        </p>
                        <div className="text-xs text-gray-500">
                          <strong>Examples:</strong> {category.examples}
                        </div>
                      </div>
                      <div className="ml-4">
                        {category.required ? (
                          <div className="bg-gray-100 text-gray-500 px-3 py-1 rounded text-sm font-medium">
                            Required
                          </div>
                        ) : (
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={preferences[category.id]}
                              onChange={(e) =>
                                setPreferences(prev => ({
                                  ...prev,
                                  [category.id]: e.target.checked
                                }))
                              }
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-orange/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-orange"></div>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCustomSave}
                    className="px-6 py-2 bg-brand-orange text-white rounded-lg hover:bg-secondary-500 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} className="h-4 w-4" />
                    <span>Save Preferences</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
